// TandMEntriesPanel.jsx
// Manages ALL entries:
//   - Entry 1 = the original T&M (top-level items/scopeOfWork/signedBy/signature)
//   - Entry 2+ = additional entries stored in tandm.entries[]
// Each entry card: view details, edit, approve/reject, download individual PDF
// New entries added via "Add Entry" button at the bottom

import { useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import CreatableSelect from "react-select/creatable";
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  DocumentTextIcon,
  DocumentArrowDownIcon,
  PhotoIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import BudgetItemsModal from "./ChangeOrderBudgetItemsModal";
import DatabaseItemsModal from "./ChangeOrderDatabaseItemsModal";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { saveAs } from "file-saver";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

const isSFUnit = (u) => {
  const s = (u || "").toLowerCase();
  return (
    s === "sf" || s.includes("square") || s.includes("sq ft") || s === "sheet"
  );
};
const isLFUnit = (u) => {
  const s = (u || "").toLowerCase();
  return s === "lf" || s.includes("linear") || s.includes("lin ft");
};
const isCoverageUnit = (u) => isSFUnit(u) || isLFUnit(u);

const parseSizeToCoverage = (sizeStr, uom) => {
  if (!sizeStr) return null;
  const str = String(sizeStr).trim().toLowerCase();
  const parseLength = (part) => {
    if (!part) return 0;
    const mixed = part.match(/^(\d+(?:\.\d+)?)' ?(\d+(?:\.\d+)?)"?$/);
    if (mixed) return parseFloat(mixed[1]) + parseFloat(mixed[2]) / 12;
    const feet = part.match(/^([\d.]+)'?$/);
    if (feet) return parseFloat(feet[1]) || 0;
    const inches = part.match(/^([\d.]+)"$/);
    if (inches) return (parseFloat(inches[1]) || 0) / 12;
    return parseFloat(part) || 0;
  };
  if (isSFUnit(uom)) {
    const parts = str.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const w = parseLength(parts[0]);
      const h = parseLength(parts[1]);
      if (w > 0 && h > 0) return w * h;
    }
    return parseLength(parts[0]) || null;
  }
  if (isLFUnit(uom)) return parseLength(str) || null;
  return null;
};

const sanitizeForDocx = (str) => {
  if (!str) return "";
  return str
    .replace(/[\u2028\u2029]/g, " ")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .replace(/[\uFFF9-\uFFFF]/g, "")
    .replace(/[^\t\n\r\x20-\uD7FF\uE000-\uFFFD]/gu, "")
    .trim();
};

const STATUS_COLORS = {
  Pending: "bg-yellow-400/20 text-yellow-400 border-yellow-400/30",
  Approved: "bg-green-400/20 text-green-400 border-green-400/30",
  Rejected: "bg-red-400/20 text-red-400 border-red-400/30",
  Draft: "bg-gray-400/20 text-gray-400 border-gray-400/30",
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#374151",
    borderColor: s.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: s.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "30px",
    fontSize: "12px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    zIndex: 9999,
  }),
  menuPortal: (p) => ({ ...p, zIndex: 999999 }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected ? "#374151" : "#1f2937",
    color: s.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#374151" },
    padding: "5px 8px",
    fontSize: "12px",
  }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb", fontSize: "12px" }),
  input: (p) => ({ ...p, color: "#e5e7eb", fontSize: "12px" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "12px" }),
  valueContainer: (p) => ({ ...p, padding: "0 6px" }),
  indicatorsContainer: (p) => ({ ...p, "& > div": { padding: "3px" } }),
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF generation for any entry (original or additional)
// ─────────────────────────────────────────────────────────────────────────────
async function generateEntryPDF({
  entryLabel,
  entryItems,
  scopeOfWork,
  signedBy,
  signatureUrl,
  entryImages,
  tandm,
  project,
  companyProfile,
}) {
  const response = await fetch("/t_and_m_template.docx");
  if (!response.ok)
    throw new Error(`Failed to load template: ${response.statusText}`);
  const zip = new PizZip(await response.arrayBuffer());

  const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
  const validBase64 =
    /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

  const base64Parser = (tagValue) => {
    if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
      return null;
    const s = tagValue.replace(base64Regex, "");
    if (!validBase64.test(s)) throw new Error("Invalid base64");
    const bin = window.atob(s);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  };

  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    modules: [
      new ImageModule({
        getImage: base64Parser,
        getSize: (img, val, name) =>
          name === "companyLogo"
            ? [150, 100]
            : name === "signature"
              ? [100, 150]
              : [200, 200],
      }),
    ],
  });

  const fetchB64 = async (url) => {
    try {
      const r = await fetch(url, { mode: "cors" });
      if (!r.ok) return null;
      const blob = await r.blob();
      return new Promise((res) => {
        const rd = new FileReader();
        rd.onloadend = () => res(rd.result);
        rd.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const logoB64 = companyProfile?.logo
    ? await fetchB64(companyProfile.logo)
    : null;
  const sigB64 = signatureUrl ? await fetchB64(signatureUrl) : null;
  const imgs = await Promise.all(
    (entryImages || []).map(async (url) => ({ image: await fetchB64(url) })),
  );

  const pdfItems = (entryItems || [])
    .filter((i) => i.forCustomer !== false)
    .map((item, idx) => {
      const uom = item.uom || "EA";
      const isCov = item.category === "M" && isCoverageUnit(uom);
      const coverage = isCov ? parseFloat(item.coveragePerUnit) || 1 : 1;
      const amount = Math.abs(item.amount || item.totalCost || 0);
      return {
        itemNo: idx + 1,
        description:
          item.customerDescription ||
          item.title ||
          item.itemDescription ||
          "Unnamed",
        unit: isCov ? "PC" : uom,
        quantity: item.quantity || item.deltaQty || 0,
        price: ((item.unitCost || 0) * coverage).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        amount: amount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
      };
    });

  if (pdfItems.length === 0)
    throw new Error("No customer-facing items to include.");

  const subtotal = pdfItems.reduce(
    (s, i) => s + (parseFloat(i.amount.replace(/[^0-9.-]+/g, "")) || 0),
    0,
  );
  const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
  const taxAmt = subtotal * (taxRate / 100);

  doc.setData({
    projectName: project?.projectName || "Unknown Project",
    date: new Date().toLocaleDateString(),
    preparedBy: tandm?.createdBy?.displayName || "Unknown",
    timeAndMaterialNumber: `${tandm?.timeAndMaterialNumber || "Draft"} – ${entryLabel}`,
    scopeOfWork: sanitizeForDocx(scopeOfWork || "N/A"),
    signedBy: signedBy || "N/A",
    companyName: companyProfile?.companyName || "",
    companyAddress: companyProfile?.address
      ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
      : "N/A",
    companyLogo: logoB64 || null,
    signature: sigB64 || null,
    items: pdfItems,
    pageBreak: "\f",
    images: imgs,
    subtotalPrice: subtotal.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    }),
    taxRate: taxRate.toFixed(2),
    taxAmount: taxAmt.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    }),
    totalPrice: (subtotal + taxAmt).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    }),
    overheadPercentage: "0.00",
  });
  doc.render();

  const docxBlob = doc.getZip().generate({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const fd = new FormData();
  fd.append("docx", docxBlob, `TandM_${tandm?._id}_${entryLabel}.docx`);
  fd.append("documentType", "TandM");
  fd.append("identifier", `${tandm?._id}_${entryLabel}`);

  const token = localStorage.getItem("token");
  const pdfRes = await axios.post(`${API_BASE}/upload/convert-to-pdf`, fd, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
    responseType: "blob",
  });
  if (pdfRes.status !== 200) throw new Error("PDF conversion failed");

  // If no images, strip the last page (blank images page) using pdf-lib
  let finalBlob = new Blob([pdfRes.data], { type: "application/pdf" });
  if (imgs.filter((i) => i.image).length === 0) {
    const { PDFDocument } = await import("pdf-lib");
    const arrayBuffer = await finalBlob.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pageCount = pdfDoc.getPageCount();
    if (pageCount > 1) pdfDoc.removePage(pageCount - 1);
    const trimmedBytes = await pdfDoc.save();
    finalBlob = new Blob([trimmedBytes], { type: "application/pdf" });
  }

  saveAs(finalBlob, `TandM_${tandm?.timeAndMaterialNumber}_${entryLabel}.pdf`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Editable items table
// ─────────────────────────────────────────────────────────────────────────────
function ItemsEditor({
  items,
  setItems,
  budgetItems,
  databaseItems,
  loadingDatabase,
  netQuantities,
  assemblyOptions,
  bidAreaOptions,
}) {
  const [showBudget, setShowBudget] = useState(false);
  const [showDatabase, setShowDatabase] = useState(false);
  const [selBudget, setSelBudget] = useState([]);
  const [selDatabase, setSelDatabase] = useState([]);
  const [dbFilters, setDbFilters] = useState({
    category: [],
    uom: [],
    search: "",
  });

  const addBudget = () => {
    if (!selBudget.length) return;
    const newItems = selBudget.map((item, idx) => {
      const key = (
        item.originalLineItemId ||
        item.lineItemId ||
        item._id ||
        item.key
      )?.toString();
      const net = netQuantities.get(key) || item;
      const isMat = item.category === "M";
      const unitCost = Number(
        (
          net.latestPrice ??
          (isMat
            ? item.cost || item.price
            : item.unitRate || item.cost || item.price) ??
          0
        ).toFixed(2),
      );
      const coverage = parseFloat(
        net.latestCoveragePerUnit ?? item.coveragePerUnit ?? 1,
      );
      const isCov = isMat && isCoverageUnit(item.uom);
      return {
        _localId: `b-${Date.now()}-${idx}`,
        itemCode:
          item.itemCode || item.materialCode || item.laborCode || item._id,
        category: item.category,
        sourceAssemblyId: item.assemblyId || null,
        sourceLineItemId: key || null,
        title: item.itemDescription || item.costCodeDescription || "",
        itemDescription: item.itemDescription || item.costCodeDescription || "",
        customerDescription:
          item.itemDescription || item.costCodeDescription || "",
        conditionName: item.conditionName || "",
        bidArea: item.bidArea || "",
        deltaQty: 1,
        quantity: 1,
        unitCost,
        amount: Number(
          (isCov ? 1 * coverage * unitCost : 1 * unitCost).toFixed(2),
        ),
        size: net.latestSize || item.size || "",
        uom: item.uom || "",
        coveragePerUnit: String(coverage),
        vendor: item.vendor?._id || item.vendor || null,
        forCustomer: true,
        forBudget: true,
      };
    });
    setItems((p) => [...p, ...newItems]);
    setSelBudget([]);
    setShowBudget(false);
  };

  const addDatabase = () => {
    if (!selDatabase.length) return;
    const newItems = selDatabase
      .map((sel, idx) => {
        const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
        if (!item) return null;
        const qty = Math.abs(sel.deltaQty || 1);
        const unitCost = item.defaultCost || 0;
        const uom = item.uom || "";
        const isCov = item.category === "M" && isCoverageUnit(uom);
        let size = sel.editedSize?.trim() || item.size?.trim() || "";
        let coverage = parseFloat(item.coveragePerUnit) || 1;
        if (isCov && size) {
          const p = parseSizeToCoverage(size, uom);
          if (p !== null) coverage = p;
        }
        return {
          _localId: `d-${Date.now()}-${idx}`,
          itemCode: item.itemCode,
          category: item.category,
          title: item.description || "",
          itemDescription: item.description || "",
          customerDescription: item.description || "",
          conditionName: sel.conditionName || "",
          bidArea: sel.bidArea || "",
          deltaQty: qty,
          quantity: qty,
          unitCost,
          amount: Number(
            (isCov ? qty * coverage * unitCost : qty * unitCost).toFixed(2),
          ),
          size,
          uom,
          coveragePerUnit: String(coverage),
          vendor: sel.selectedVendor || null,
          forCustomer: true,
          forBudget: true,
        };
      })
      .filter(Boolean);
    setItems((p) => [...p, ...newItems]);
    setSelDatabase([]);
    setShowDatabase(false);
  };

  const updateItem = (localId, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item._localId !== localId) return item;
        const u = { ...item };
        if (field === "deltaQty") {
          const v = Math.max(0, parseFloat(value) || 0);
          u.deltaQty = v;
          u.quantity = v;
        } else {
          u[field] = value;
        }
        const qty = Math.abs(u.deltaQty || u.quantity || 0);
        const isCov = u.category === "M" && isCoverageUnit(u.uom);
        const cov = parseFloat(u.coveragePerUnit) || 1;
        u.amount = isCov
          ? qty * cov * (u.unitCost || 0)
          : qty * (u.unitCost || 0);
        return u;
      }),
    );
  };

  const getDbFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      databaseItems.forEach((i) => {
        const v = field === "uom" ? i.uom : i[field];
        if (v) unique.add(v);
      });
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v }));
    },
    [databaseItems],
  );

  const total = useMemo(
    () => items.reduce((s, i) => s + (i.amount || 0), 0),
    [items],
  );

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setShowBudget(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-md hover:bg-orange-500/20 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" /> From Budget
        </button>
        <button
          type="button"
          onClick={() => setShowDatabase(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-md hover:bg-blue-500/20 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" /> From Database
        </button>
      </div>

      {items.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-700 mb-2">
          <table className="min-w-full divide-y divide-gray-700 text-xs">
            <thead className="bg-gray-900/60">
              <tr>
                <th className="px-3 py-2 text-left text-gray-400 uppercase font-semibold tracking-wider min-w-[140px]">
                  Description
                </th>
                <th className="px-3 py-2 text-center text-gray-400 uppercase font-semibold tracking-wider">
                  Cat
                </th>
                <th className="px-3 py-2 text-left text-gray-400 uppercase font-semibold tracking-wider min-w-[150px]">
                  Assembly
                </th>
                <th className="px-3 py-2 text-left text-gray-400 uppercase font-semibold tracking-wider min-w-[130px]">
                  Bid Area
                </th>
                <th className="px-3 py-2 text-right text-gray-400 uppercase font-semibold tracking-wider">
                  Unit $
                </th>
                <th className="px-3 py-2 text-right text-gray-400 uppercase font-semibold tracking-wider w-20">
                  Qty
                </th>
                <th className="px-3 py-2 text-right text-gray-400 uppercase font-semibold tracking-wider">
                  Total
                </th>
                <th className="px-3 py-2 text-center text-gray-400 uppercase font-semibold tracking-wider">
                  Cust
                </th>
                <th className="px-3 py-2 text-center text-gray-400 uppercase font-semibold tracking-wider">
                  Bdgt
                </th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {items.map((item) => (
                <tr
                  key={item._localId}
                  className="hover:bg-gray-700/30 transition-colors"
                >
                  <td className="px-3 py-1.5 text-gray-300">
                    {item.customerDescription || item.title || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${item.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
                    >
                      {item.category === "M" ? "Mat" : "Lab"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <CreatableSelect
                      options={assemblyOptions}
                      value={
                        assemblyOptions.find(
                          (o) => o.value === item.conditionName,
                        ) ||
                        (item.conditionName
                          ? {
                              value: item.conditionName,
                              label: item.conditionName,
                            }
                          : null)
                      }
                      onChange={(o) =>
                        updateItem(
                          item._localId,
                          "conditionName",
                          o ? o.value : "",
                        )
                      }
                      onCreateOption={(v) =>
                        updateItem(item._localId, "conditionName", v)
                      }
                      placeholder="Select or create..."
                      isClearable
                      formatCreateLabel={(v) => `Create: "${v}"`}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <CreatableSelect
                      options={bidAreaOptions}
                      value={
                        bidAreaOptions.find((o) => o.value === item.bidArea) ||
                        (item.bidArea
                          ? { value: item.bidArea, label: item.bidArea }
                          : null)
                      }
                      onChange={(o) =>
                        updateItem(item._localId, "bidArea", o ? o.value : "")
                      }
                      onCreateOption={(v) =>
                        updateItem(item._localId, "bidArea", v)
                      }
                      placeholder="Select or create..."
                      isClearable
                      formatCreateLabel={(v) => `Create: "${v}"`}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-right text-gray-300">
                    {fmt(item.unitCost)}
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      value={item.deltaQty || 0}
                      min="0"
                      onChange={(e) =>
                        updateItem(item._localId, "deltaQty", e.target.value)
                      }
                      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-gray-200 text-xs text-right focus:border-orange-500 focus:outline-none"
                    />
                  </td>
                  <td className="px-3 py-1.5 text-right font-medium text-gray-200">
                    {fmt(item.amount)}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={item.forCustomer !== false}
                      onChange={(e) =>
                        updateItem(
                          item._localId,
                          "forCustomer",
                          e.target.checked,
                        )
                      }
                      className="w-3.5 h-3.5 rounded border-gray-500 text-orange-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={item.forBudget !== false}
                      onChange={(e) =>
                        updateItem(item._localId, "forBudget", e.target.checked)
                      }
                      className="w-3.5 h-3.5 rounded border-gray-500 text-orange-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() =>
                        setItems((p) =>
                          p.filter((i) => i._localId !== item._localId),
                        )
                      }
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-600 bg-gray-800/40">
                <td
                  colSpan={6}
                  className="px-3 py-2 text-right text-xs text-gray-400 font-medium"
                >
                  Budget Total:
                </td>
                <td className="px-3 py-2 text-right text-sm font-bold text-green-400">
                  {fmt(total)}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 text-xs border border-dashed border-gray-600 rounded-lg mb-2">
          No items yet. Use the buttons above to add items.
        </div>
      )}

      <BudgetItemsModal
        open={showBudget}
        onClose={() => {
          setShowBudget(false);
          setSelBudget([]);
        }}
        budgetItems={budgetItems}
        loading={false}
        selectedItems={selBudget}
        setSelectedItems={setSelBudget}
        onAddItems={addBudget}
        formatCurrency={fmt}
        getFilterOptions={() => []}
        netQuantities={netQuantities}
        netLoading={false}
      />
      <DatabaseItemsModal
        open={showDatabase}
        onClose={() => {
          setShowDatabase(false);
          setSelDatabase([]);
          setDbFilters({ category: [], uom: [], search: "" });
        }}
        databaseItems={databaseItems}
        loading={loadingDatabase}
        databaseFilters={dbFilters}
        setDatabaseFilters={setDbFilters}
        selectedDatabaseItems={selDatabase}
        setSelectedDatabaseItems={setSelDatabase}
        onAddItems={addDatabase}
        formatCurrency={fmt}
        formData={{}}
        parseSizeToCoverage={parseSizeToCoverage}
        isSFUnit={isSFUnit}
        isLFUnit={isLFUnit}
        getDatabaseFilterOptions={getDbFilterOptions}
        netQuantities={netQuantities}
        netLoading={false}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Read-only items table
// ─────────────────────────────────────────────────────────────────────────────
function ItemsView({ items }) {
  const total = useMemo(
    () =>
      items
        .filter((i) => i.forBudget !== false)
        .reduce((s, i) => s + Math.abs(i.amount || i.totalCost || 0), 0),
    [items],
  );
  if (!items?.length)
    return <p className="text-xs text-gray-500 italic py-2">No items.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-700">
      <table className="min-w-full divide-y divide-gray-700 text-xs">
        <thead className="bg-gray-900/60">
          <tr>
            <th className="px-3 py-2 text-left text-gray-400 uppercase font-semibold tracking-wider">
              Description
            </th>
            <th className="px-3 py-2 text-center text-gray-400 uppercase font-semibold tracking-wider">
              Cat
            </th>
            <th className="px-3 py-2 text-left text-gray-400 uppercase font-semibold tracking-wider">
              Assembly
            </th>
            <th className="px-3 py-2 text-left text-gray-400 uppercase font-semibold tracking-wider">
              Bid Area
            </th>
            <th className="px-3 py-2 text-right text-gray-400 uppercase font-semibold tracking-wider">
              Unit $
            </th>
            <th className="px-3 py-2 text-right text-gray-400 uppercase font-semibold tracking-wider">
              Qty
            </th>
            <th className="px-3 py-2 text-right text-gray-400 uppercase font-semibold tracking-wider">
              Total
            </th>
            <th className="px-3 py-2 text-center text-gray-400 uppercase font-semibold tracking-wider">
              Cust
            </th>
            <th className="px-3 py-2 text-center text-gray-400 uppercase font-semibold tracking-wider">
              Bdgt
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {items.map((item, idx) => (
            <tr
              key={item._id || idx}
              className="hover:bg-gray-700/20 transition-colors"
            >
              <td className="px-3 py-2 text-gray-300">
                {item.customerDescription || item.title || "—"}
              </td>
              <td className="px-3 py-2 text-center">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${item.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
                >
                  {item.category === "M" ? "Mat" : "Lab"}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-300">
                {item.conditionName || "—"}
              </td>
              <td className="px-3 py-2 text-gray-300">{item.bidArea || "—"}</td>
              <td className="px-3 py-2 text-right text-gray-300">
                {fmt(item.unitCost)}
              </td>
              <td className="px-3 py-2 text-right text-gray-200 font-medium">
                {item.quantity || item.deltaQty || 0}
              </td>
              <td className="px-3 py-2 text-right font-medium text-gray-200">
                {fmt(Math.abs(item.amount || item.totalCost || 0))}
              </td>
              <td className="px-3 py-2 text-center">
                {item.forCustomer !== false ? (
                  <span className="text-green-400">✓</span>
                ) : (
                  <span className="text-gray-600">✗</span>
                )}
              </td>
              <td className="px-3 py-2 text-center">
                {item.forBudget !== false ? (
                  <span className="text-green-400">✓</span>
                ) : (
                  <span className="text-gray-600">✗</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-600 bg-gray-800/40">
            <td
              colSpan={6}
              className="px-3 py-2 text-right text-xs text-gray-400 font-medium"
            >
              Budget Total:
            </td>
            <td className="px-3 py-2 text-right text-sm font-bold text-green-400">
              {fmt(total)}
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Signature upload widget
// ─────────────────────────────────────────────────────────────────────────────
function SignatureField({ file, setFile, preview, setPreview, existingUrl }) {
  const ref = useRef(null);
  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const clear = () => {
    setFile(null);
    setPreview(null);
    if (ref.current) ref.current.value = "";
  };
  const shown = preview || existingUrl;
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">
        Signature
      </label>
      <div className="flex items-start gap-3">
        {shown ? (
          <div className="relative group">
            <img
              src={shown}
              alt="Signature"
              className="h-16 w-36 object-contain bg-white rounded-lg border border-gray-600 p-1"
            />
            <button
              type="button"
              onClick={clear}
              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        ) : (
          <div
            onClick={() => ref.current?.click()}
            className="h-16 w-36 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition-colors"
          >
            <PhotoIcon className="h-5 w-5 text-gray-600 mb-1" />
            <span className="text-[10px] text-gray-500">Upload signature</span>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 transition-colors"
          >
            {shown ? "Replace" : "Upload"}
          </button>
          <span className="text-[10px] text-gray-500">PNG or JPG</span>
        </div>
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit form fields shared by Entry 1 and Entry N
// ─────────────────────────────────────────────────────────────────────────────
function EntryEditForm({
  name,
  setName,
  showNameField = true,
  note,
  setNote,
  scopeOfWork,
  setScopeOfWork,
  signedBy,
  setSignedBy,
  sigFile,
  setSigFile,
  sigPreview,
  setSigPreview,
  existingSigUrl,
  items,
  setItems,
  budgetItems,
  databaseItems,
  loadingDatabase,
  netQuantities,
  assemblyOptions,
  bidAreaOptions,
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showNameField && (
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Entry Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Entry 2, Extra Work May 15"
              className="w-full rounded-md bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none transition-colors"
            />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Approved By (Signed By) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            placeholder="Name of person approving"
            className="w-full rounded-md bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">
          Scope of Work
        </label>
        <textarea
          value={scopeOfWork}
          onChange={(e) => setScopeOfWork(e.target.value)}
          rows={2}
          placeholder="Describe the work completed..."
          className="w-full rounded-md bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none transition-colors resize-none"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">
          Internal Note
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional..."
          className="w-full rounded-md bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none transition-colors"
        />
      </div>
      <SignatureField
        file={sigFile}
        setFile={setSigFile}
        preview={sigPreview}
        setPreview={setSigPreview}
        existingUrl={existingSigUrl}
      />
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-2">
          Items <span className="text-red-400">*</span>
        </label>
        <ItemsEditor
          items={items}
          setItems={setItems}
          budgetItems={budgetItems}
          databaseItems={databaseItems}
          loadingDatabase={loadingDatabase}
          netQuantities={netQuantities}
          assemblyOptions={assemblyOptions}
          bidAreaOptions={bidAreaOptions}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main exported panel
// ─────────────────────────────────────────────────────────────────────────────
export default function TandMEntriesPanel({
  tandm,
  projectId,
  token,
  showAlert,
  onTandMChange,
  budgetItems,
  databaseItems,
  loadingDatabase,
  netQuantities,
  fetchNetQuantities,
  fetchDatabaseItems,
  companyProfile,
  project,
  assemblyOptions: assemblyProp,
  bidAreaOptions: bidAreaProp,
}) {
  const headers = { Authorization: `Bearer ${token}` };

  // Derive dropdown options from all items across all entries
  const assemblyOptions = useMemo(() => {
    const u = new Set();
    (tandm?.items || []).forEach((i) => {
      if (i.conditionName) u.add(i.conditionName);
    });
    (tandm?.entries || []).forEach((e) =>
      (e.items || []).forEach((i) => {
        if (i.conditionName) u.add(i.conditionName);
      }),
    );
    budgetItems.forEach((i) => {
      if (i.conditionName) u.add(i.conditionName);
    });
    (assemblyProp || []).forEach((o) => {
      if (o.value) u.add(o.value);
    });
    return Array.from(u)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [tandm, budgetItems, assemblyProp]);

  const bidAreaOptions = useMemo(() => {
    const u = new Set();
    (tandm?.items || []).forEach((i) => {
      if (i.bidArea) u.add(i.bidArea);
    });
    (tandm?.entries || []).forEach((e) =>
      (e.items || []).forEach((i) => {
        if (i.bidArea) u.add(i.bidArea);
      }),
    );
    budgetItems.forEach((i) => {
      if (i.bidArea) u.add(i.bidArea);
    });
    (bidAreaProp || []).forEach((o) => {
      if (o.value) u.add(o.value);
    });
    return Array.from(u)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [tandm, budgetItems, bidAreaProp]);

  const entries = tandm?.entries || [];

  // Which entry card is expanded (null = none, "original" = Entry 1, or entry._id)
  const [expanded, setExpanded] = useState("original");
  // Which entry is in edit mode ("original" or entry._id)
  const [editing, setEditing] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Draft state for editing Entry 1 (original)
  const [e1ScopeOfWork, setE1ScopeOfWork] = useState("");
  const [e1SignedBy, setE1SignedBy] = useState("");
  const [e1Note, setE1Note] = useState("");
  const [e1SigFile, setE1SigFile] = useState(null);
  const [e1SigPreview, setE1SigPreview] = useState(null);
  const [e1Items, setE1Items] = useState([]);

  // Draft state for editing / creating additional entries
  const [draftName, setDraftName] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [draftScopeOfWork, setDraftScopeOfWork] = useState("");
  const [draftSignedBy, setDraftSignedBy] = useState("");
  const [draftSigFile, setDraftSigFile] = useState(null);
  const [draftSigPreview, setDraftSigPreview] = useState(null);
  const [draftExistingSig, setDraftExistingSig] = useState(null);
  const [draftItems, setDraftItems] = useState([]);

  const toggleExpand = (id) => setExpanded((prev) => (prev === id ? null : id));

  const ensureLoaded = () => {
    if (netQuantities.size === 0) fetchNetQuantities();
    if (databaseItems.length === 0) fetchDatabaseItems();
  };

  // ── Edit Entry 1 ────────────────────────────────────────────────────────────
  const startEditOriginal = () => {
    ensureLoaded();
    setE1ScopeOfWork(tandm?.scopeOfWork || "");
    setE1SignedBy(tandm?.signedBy || "");
    setE1Note("");
    setE1SigFile(null);
    setE1SigPreview(null);
    setE1Items(
      (tandm?.items || []).map((i, idx) => ({
        ...i,
        _localId: i._id?.toString() || `orig-${idx}`,
      })),
    );
    setEditing("original");
    setExpanded("original");
  };

  const cancelEdit = () => {
    setEditing(null);
    setCreatingNew(false);
    resetDraft();
  };

  const resetDraft = () => {
    setDraftName("");
    setDraftNote("");
    setDraftScopeOfWork("");
    setDraftSignedBy("");
    setDraftSigFile(null);
    setDraftSigPreview(null);
    setDraftExistingSig(null);
    setDraftItems([]);
  };

  const validateOriginal = () => {
    if (!e1SignedBy.trim()) {
      showAlert("error", "Validation Error", "Approved By is required.");
      return false;
    }
    if (!e1Items.length) {
      showAlert("error", "Validation Error", "At least one item is required.");
      return false;
    }
    const miss = e1Items.filter(
      (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
    );
    if (miss.length) {
      showAlert(
        "error",
        "Missing Fields",
        `${miss.length} item(s) missing Assembly or Bid Area.`,
      );
      return false;
    }
    return true;
  };

  const validateDraft = () => {
    if (!draftName.trim()) {
      showAlert("error", "Validation Error", "Entry name is required.");
      return false;
    }
    if (!draftSignedBy.trim()) {
      showAlert("error", "Validation Error", "Approved By is required.");
      return false;
    }
    if (!draftItems.length) {
      showAlert("error", "Validation Error", "At least one item is required.");
      return false;
    }
    const miss = draftItems.filter(
      (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
    );
    if (miss.length) {
      showAlert(
        "error",
        "Missing Fields",
        `${miss.length} item(s) missing Assembly or Bid Area.`,
      );
      return false;
    }
    return true;
  };

  // Save edits to Entry 1 — uses the main PUT /:projectId/:id route
  const saveOriginal = async () => {
    if (!validateOriginal()) return;
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append(
        "items",
        JSON.stringify(e1Items.map(({ _localId, ...i }) => i)),
      );
      fd.append("scopeOfWork", e1ScopeOfWork);
      fd.append("signedBy", e1SignedBy);
      fd.append("overheadPercentage", tandm?.overheadPercentage || 0);
      if (e1SigFile instanceof File) fd.append("signature", e1SigFile);
      const res = await axios.put(
        `${API_BASE}/time-and-material2/${projectId}/${tandm._id}`,
        fd,
        { headers },
      );
      onTandMChange(res.data);
      setEditing(null);
      showAlert("success", "Saved", "Entry 1 updated successfully.");
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Save a new additional entry
  const saveNewEntry = async () => {
    if (!validateDraft()) return;
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", draftName);
      fd.append("note", draftNote);
      fd.append("scopeOfWork", draftScopeOfWork);
      fd.append("signedBy", draftSignedBy);
      fd.append(
        "items",
        JSON.stringify(draftItems.map(({ _localId, ...i }) => i)),
      );
      if (draftSigFile instanceof File) fd.append("signature", draftSigFile);
      const res = await axios.post(
        `${API_BASE}/time-and-material2/${projectId}/${tandm._id}/entries`,
        fd,
        { headers },
      );
      onTandMChange(res.data.tandm);
      setCreatingNew(false);
      resetDraft();
      const ne = res.data.entry;
      if (ne?._id) setExpanded(ne._id);
      showAlert("success", "Entry Added", "New entry saved.");
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save entry.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Save edits to an additional entry
  const saveEditEntry = async (entryId) => {
    if (!validateDraft()) return;
    setIsSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", draftName);
      fd.append("note", draftNote);
      fd.append("scopeOfWork", draftScopeOfWork);
      fd.append("signedBy", draftSignedBy);
      fd.append(
        "items",
        JSON.stringify(draftItems.map(({ _localId, ...i }) => i)),
      );
      if (draftSigFile instanceof File) fd.append("signature", draftSigFile);
      const res = await axios.put(
        `${API_BASE}/time-and-material2/${projectId}/${tandm._id}/entries/${entryId}`,
        fd,
        { headers },
      );
      onTandMChange(res.data.tandm);
      setEditing(null);
      resetDraft();
      showAlert("success", "Updated", "Entry updated.");
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to update entry.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const startEditEntry = (entry) => {
    ensureLoaded();
    setDraftName(entry.name || "");
    setDraftNote(entry.note || "");
    setDraftScopeOfWork(entry.scopeOfWork || "");
    setDraftSignedBy(entry.signedBy || "");
    setDraftSigFile(null);
    setDraftSigPreview(null);
    setDraftExistingSig(entry.signature || null);
    setDraftItems(
      (entry.items || []).map((i, idx) => ({
        ...i,
        _localId: i._id?.toString() || `e-${idx}`,
      })),
    );
    setEditing(entry._id);
    setExpanded(entry._id);
  };

  const startCreateNew = () => {
    ensureLoaded();
    resetDraft();
    setDraftName(`Entry ${entries.length + 2}`);
    setCreatingNew(true);
    setEditing(null);
  };

  const handleStatusChange = async (entryId, status) => {
    setConfirmAction(null);
    try {
      const res = await axios.patch(
        `${API_BASE}/time-and-material2/${projectId}/${tandm._id}/entries/${entryId}/status`,
        { status },
        { headers },
      );
      onTandMChange(res.data.tandm);
      showAlert(
        "success",
        status === "Approved" ? "Approved" : "Rejected",
        res.data.message,
      );
    } catch (err) {
      showAlert("error", "Error", err.response?.data?.message || "Failed.");
    }
  };

  // Approve/Reject Entry 1 (the original T&M) via the main status route
  const handleOriginalStatusChange = async (status) => {
    setConfirmAction(null);
    try {
      const res = await axios.patch(
        `${API_BASE}/time-and-material2/${projectId}/${tandm._id}/status`,
        { status },
        { headers },
      );
      onTandMChange(res.data.tandm);
      showAlert(
        "success",
        status === "Approved" ? "Entry 1 Approved" : "Entry 1 Rejected",
        res.data.message,
      );
    } catch (err) {
      showAlert("error", "Error", err.response?.data?.message || "Failed.");
    }
  };

  const handleDownloadPDF = async (
    entryLabel,
    entryItems,
    scopeOfWork,
    signedBy,
    signatureUrl,
    images,
    entryId,
  ) => {
    setGeneratingPDF(entryId);
    try {
      await generateEntryPDF({
        entryLabel,
        entryItems,
        scopeOfWork,
        signedBy,
        signatureUrl,
        entryImages: images,
        tandm,
        project,
        companyProfile,
      });
    } catch (err) {
      showAlert("error", "PDF Error", err.message);
    } finally {
      setGeneratingPDF(null);
    }
  };

  // ── Entry 1 budget total ────────────────────────────────────────────────────
  const originalBudgetTotal = useMemo(
    () =>
      (tandm?.items || [])
        .filter((i) => i.forBudget !== false)
        .reduce((s, i) => s + Math.abs(i.amount || i.totalCost || 0), 0),
    [tandm],
  );

  const isAnyEditing = editing !== null || creatingNew;

  return (
    <div className="space-y-3">
      {/* Confirm dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-gray-900/75 flex items-center justify-center z-50">
          <div className="bg-gray-700 rounded-lg border border-gray-600 p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-base font-semibold text-white mb-2">
              {confirmAction.status === "Approved"
                ? "Approve Entry"
                : "Reject Entry"}
            </h3>
            <p className="text-sm text-gray-300 mb-4">
              {confirmAction.status === "Approved"
                ? "Approving will create a new budget for these items. Other entries are not affected."
                : confirmAction.entryId === "original"
                  ? "Rejecting Entry 1 only changes its status. Any approved additional entries are not affected."
                  : "Are you sure you want to reject this entry? The rest of the T&M is not affected."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  confirmAction.entryId === "original"
                    ? handleOriginalStatusChange(confirmAction.status)
                    : handleStatusChange(
                        confirmAction.entryId,
                        confirmAction.status,
                      )
                }
                className={`px-4 py-2 text-sm text-white rounded-md transition-colors ${confirmAction.status === "Approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
              >
                {confirmAction.status === "Approved" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ENTRY 1 (ORIGINAL) ── */}
      <div
        className={`bg-gray-800 rounded-lg border overflow-hidden ${tandm?.status === "Approved" ? "border-green-500/30" : tandm?.status === "Rejected" ? "border-red-500/30" : "border-gray-600"}`}
      >
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => toggleExpand("original")}
            className="text-gray-400 hover:text-orange-400 flex-shrink-0 transition-colors"
          >
            {expanded === "original" ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white">
                Entry 1 — Original
              </span>
              <span
                className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${STATUS_COLORS[tandm?.status] || STATUS_COLORS.Pending}`}
              >
                {tandm?.status || "Pending"}
              </span>
              <span className="text-xs text-gray-500">
                {(tandm?.items || []).length} items
              </span>
              {tandm?.signedBy && (
                <span className="text-xs text-gray-500">
                  · Signed: {tandm.signedBy}
                </span>
              )}
              {tandm?.signedAt && (
                <span className="text-xs text-gray-500">
                  · {new Date(tandm.signedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {tandm?.scopeOfWork && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {tandm.scopeOfWork}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                Budget $
              </div>
              <div className="text-sm font-semibold text-blue-400">
                {fmt(originalBudgetTotal)}
              </div>
            </div>
            {!isAnyEditing && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={generatingPDF === "original"}
                  onClick={() =>
                    handleDownloadPDF(
                      "Entry-1",
                      tandm?.items || [],
                      tandm?.scopeOfWork,
                      tandm?.signedBy,
                      tandm?.signature,
                      tandm?.images || [],
                      "original",
                    )
                  }
                  className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors disabled:opacity-50"
                >
                  <DocumentArrowDownIcon className="h-3.5 w-3.5" />{" "}
                  {generatingPDF === "original" ? "..." : "PDF"}
                </button>
                {(tandm?.status === "Pending" ||
                  tandm?.status === "Rejected") && (
                  <button
                    type="button"
                    onClick={startEditOriginal}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors"
                  >
                    <PencilIcon className="h-3 w-3" /> Edit
                  </button>
                )}
                {(tandm?.status === "Pending" ||
                  tandm?.status === "Rejected") && (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        entryId: "original",
                        status: "Approved",
                      })
                    }
                    className="flex items-center gap-1 px-2 py-1 text-xs text-green-400 hover:text-green-300 font-medium transition-colors"
                  >
                    <CheckCircleIcon className="h-3.5 w-3.5" /> Approve
                  </button>
                )}
                {tandm?.status === "Pending" && (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        entryId: "original",
                        status: "Rejected",
                      })
                    }
                    className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
                  >
                    <XCircleIcon className="h-3.5 w-3.5" /> Reject
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Expanded body */}
        {expanded === "original" && (
          <div className="border-t border-gray-700 p-4">
            {editing === "original" ? (
              <>
                <EntryEditForm
                  showNameField={false}
                  name=""
                  setName={() => {}}
                  note={e1Note}
                  setNote={setE1Note}
                  scopeOfWork={e1ScopeOfWork}
                  setScopeOfWork={setE1ScopeOfWork}
                  signedBy={e1SignedBy}
                  setSignedBy={setE1SignedBy}
                  sigFile={e1SigFile}
                  setSigFile={setE1SigFile}
                  sigPreview={e1SigPreview}
                  setSigPreview={setE1SigPreview}
                  existingSigUrl={tandm?.signature || null}
                  items={e1Items}
                  setItems={setE1Items}
                  budgetItems={budgetItems}
                  databaseItems={databaseItems}
                  loadingDatabase={loadingDatabase}
                  netQuantities={netQuantities}
                  assemblyOptions={assemblyOptions}
                  bidAreaOptions={bidAreaOptions}
                />
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveOriginal}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? "Saving..." : "Save Entry 1"}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                {(tandm?.scopeOfWork || tandm?.signature) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {tandm.scopeOfWork && (
                      <div className="bg-gray-700/40 rounded-md p-3">
                        <span className="text-xs text-gray-400 font-medium block mb-1">
                          Scope of Work
                        </span>
                        <span className="text-xs text-gray-300">
                          {tandm.scopeOfWork}
                        </span>
                      </div>
                    )}
                    {tandm.signature && (
                      <div className="bg-gray-700/40 rounded-md p-3">
                        <span className="text-xs text-gray-400 font-medium block mb-1">
                          Signature
                        </span>
                        <img
                          src={tandm.signature}
                          alt="Signature"
                          className="h-14 object-contain bg-white rounded border border-gray-600 p-1"
                        />
                      </div>
                    )}
                  </div>
                )}
                <ItemsView items={tandm?.items || []} />
                {tandm?.images?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-2">
                      Images
                    </p>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                      {tandm.images.map((img, i) => (
                        <a
                          key={i}
                          href={img}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={img}
                            alt={`img ${i + 1}`}
                            className="w-full h-20 object-cover rounded border border-gray-600 hover:border-orange-400 transition-colors"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── ADDITIONAL ENTRIES ── */}
      {entries.map((entry) => {
        const isExp = expanded === entry._id;
        const isEdit = editing === entry._id;
        const entryTotal = (entry.items || [])
          .filter((i) => i.forBudget !== false)
          .reduce((s, i) => s + Math.abs(i.amount || i.totalCost || 0), 0);
        const canEdit = ["Pending", "Draft", "Rejected"].includes(entry.status);
        const canApprove =
          entry.status === "Pending" || entry.status === "Rejected";
        const canReject =
          entry.status === "Pending" || entry.status === "Draft";
        const isPDFing = generatingPDF === entry._id;

        return (
          <div
            key={entry._id}
            className={`bg-gray-800 rounded-lg border overflow-hidden ${entry.status === "Approved" ? "border-green-500/30" : entry.status === "Rejected" ? "border-red-500/30" : "border-gray-600"}`}
          >
            <div className="px-4 py-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggleExpand(entry._id)}
                className="text-gray-400 hover:text-orange-400 flex-shrink-0 transition-colors"
              >
                {isExp ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white">
                    {entry.name}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${STATUS_COLORS[entry.status] || STATUS_COLORS.Pending}`}
                  >
                    {entry.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(entry.items || []).length} items
                  </span>
                  {entry.signedBy && (
                    <span className="text-xs text-gray-500">
                      · Signed: {entry.signedBy}
                    </span>
                  )}
                  {entry.createdAt && (
                    <span className="text-xs text-gray-500">
                      · {new Date(entry.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {entry.scopeOfWork && (
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {entry.scopeOfWork}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                    Budget $
                  </div>
                  <div className="text-sm font-semibold text-green-400">
                    {fmt(entryTotal)}
                  </div>
                </div>
                {!isEdit && !isAnyEditing && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={isPDFing}
                      onClick={() =>
                        handleDownloadPDF(
                          entry.name,
                          entry.items || [],
                          entry.scopeOfWork,
                          entry.signedBy,
                          entry.signature,
                          entry.images || [],
                          entry._id,
                        )
                      }
                      className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors disabled:opacity-50"
                    >
                      <DocumentArrowDownIcon className="h-3.5 w-3.5" />{" "}
                      {isPDFing ? "..." : "PDF"}
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => startEditEntry(entry)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors"
                      >
                        <PencilIcon className="h-3 w-3" /> Edit
                      </button>
                    )}
                    {canApprove && (
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({
                            entryId: entry._id,
                            status: "Approved",
                          })
                        }
                        className="flex items-center gap-1 px-2 py-1 text-xs text-green-400 hover:text-green-300 font-medium transition-colors"
                      >
                        <CheckCircleIcon className="h-3.5 w-3.5" /> Approve
                      </button>
                    )}
                    {canReject && (
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmAction({
                            entryId: entry._id,
                            status: "Rejected",
                          })
                        }
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
                      >
                        <XCircleIcon className="h-3.5 w-3.5" /> Reject
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {isExp && (
              <div className="border-t border-gray-700 p-4">
                {isEdit ? (
                  <>
                    <EntryEditForm
                      name={draftName}
                      setName={setDraftName}
                      note={draftNote}
                      setNote={setDraftNote}
                      scopeOfWork={draftScopeOfWork}
                      setScopeOfWork={setDraftScopeOfWork}
                      signedBy={draftSignedBy}
                      setSignedBy={setDraftSignedBy}
                      sigFile={draftSigFile}
                      setSigFile={setDraftSigFile}
                      sigPreview={draftSigPreview}
                      setSigPreview={setDraftSigPreview}
                      existingSigUrl={draftExistingSig}
                      items={draftItems}
                      setItems={setDraftItems}
                      budgetItems={budgetItems}
                      databaseItems={databaseItems}
                      loadingDatabase={loadingDatabase}
                      netQuantities={netQuantities}
                      assemblyOptions={assemblyOptions}
                      bidAreaOptions={bidAreaOptions}
                    />
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-700">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEditEntry(entry._id)}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50 transition-colors"
                      >
                        {isSaving ? "Saving..." : "Update Entry"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    {(entry.scopeOfWork || entry.note || entry.signature) && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {entry.scopeOfWork && (
                          <div className="bg-gray-700/40 rounded-md p-3">
                            <span className="text-xs text-gray-400 font-medium block mb-1">
                              Scope of Work
                            </span>
                            <span className="text-xs text-gray-300">
                              {entry.scopeOfWork}
                            </span>
                          </div>
                        )}
                        {entry.note && (
                          <div className="bg-gray-700/40 rounded-md p-3">
                            <span className="text-xs text-gray-400 font-medium block mb-1">
                              Note
                            </span>
                            <span className="text-xs text-gray-300">
                              {entry.note}
                            </span>
                          </div>
                        )}
                        {entry.signature && (
                          <div className="bg-gray-700/40 rounded-md p-3">
                            <span className="text-xs text-gray-400 font-medium block mb-1">
                              Signature
                            </span>
                            <img
                              src={entry.signature}
                              alt="Signature"
                              className="h-14 object-contain bg-white rounded border border-gray-600 p-1"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <ItemsView items={entry.items || []} />
                    {entry.images?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium mb-2">
                          Images
                        </p>
                        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                          {entry.images.map((img, i) => (
                            <a
                              key={i}
                              href={img}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={img}
                                alt={`img ${i + 1}`}
                                className="w-full h-20 object-cover rounded border border-gray-600 hover:border-orange-400 transition-colors"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {entry.status === "Approved" && entry.approvedAt && (
                      <p className="text-xs text-gray-500">
                        Approved{" "}
                        {entry.approvedBy?.displayName
                          ? `by ${entry.approvedBy.displayName} `
                          : ""}
                        on {new Date(entry.approvedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ── NEW ENTRY FORM ── */}
      {creatingNew && (
        <div className="bg-gray-800 rounded-lg border border-orange-500/40 overflow-hidden">
          <div className="px-4 py-3 bg-orange-500/10 border-b border-orange-500/20 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-orange-400">New Entry</h4>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-gray-400 hover:text-gray-200"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4">
            <EntryEditForm
              name={draftName}
              setName={setDraftName}
              note={draftNote}
              setNote={setDraftNote}
              scopeOfWork={draftScopeOfWork}
              setScopeOfWork={setDraftScopeOfWork}
              signedBy={draftSignedBy}
              setSignedBy={setDraftSignedBy}
              sigFile={draftSigFile}
              setSigFile={setDraftSigFile}
              sigPreview={draftSigPreview}
              setSigPreview={setDraftSigPreview}
              existingSigUrl={null}
              items={draftItems}
              setItems={setDraftItems}
              budgetItems={budgetItems}
              databaseItems={databaseItems}
              loadingDatabase={loadingDatabase}
              netQuantities={netQuantities}
              assemblyOptions={assemblyOptions}
              bidAreaOptions={bidAreaOptions}
            />
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveNewEntry}
                disabled={isSaving}
                className="px-4 py-2 text-sm text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50 transition-colors"
              >
                {isSaving ? "Saving..." : "Save Entry"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD ENTRY BUTTON ── */}
      {!creatingNew && !editing && (
        <button
          type="button"
          onClick={startCreateNew}
          className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-sm text-gray-400 hover:text-orange-400 hover:border-orange-500/50 transition-colors flex items-center justify-center gap-2"
        >
          <PlusIcon className="h-4 w-4" /> Add Entry
        </button>
      )}
    </div>
  );
}
